import React, { useState } from 'react';
import { CurrencyDollarIcon } from '@heroicons/react/24/outline';
import inventoryApi from '../../../services/inventoryApi';
import { downloadXlsxFromAoa } from '../../../utils/excelJsSheetHelpers';
import StockValuationReport from './StockValuationReport';
import {
  ReportShell, Field, DateInput, WarehouseSelect, ProductFilters, EMPTY_PRODUCT, productParams, productFilterLine,
  ExportButtons, printReport, runReport, qty, money, today, monthStart, fmtDate, esc,
} from './shared/ReportKit';

const BLOCKS = [['opening', 'Opening'], ['received', 'Received'], ['issued', 'Issued'], ['closing', 'Closing']];

// Stock Value for a period: Opening + Received − Issued = Closing (quantity, price and value per item)
const StockValuePeriod = () => {
  const [startDate, setStartDate] = useState(monthStart());
  const [endDate, setEndDate] = useState(today());
  const [warehouse, setWarehouse] = useState({ id: '', label: '' });
  const [product, setProduct] = useState(EMPTY_PRODUCT);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const generate = () => runReport(() => inventoryApi.getStockValuePeriod({
    startDate, endDate, ...(warehouse.id ? { warehouseId: warehouse.id } : {}), ...productParams(product),
  }), setLoading, setData);
  const reset = () => { setStartDate(monthStart()); setEndDate(today()); setWarehouse({ id: '', label: '' }); setProduct(EMPTY_PRODUCT); setData(null); };
  const period = data ? `${fmtDate(data.startDate)} — ${fmtDate(data.endDate)}` : '';
  const filtersLine = [warehouse.label && `Location: ${warehouse.label}`, productFilterLine(product)].filter(Boolean).join(' · ');

  const exportExcel = async () => {
    const rows = [['STOCK VALUE REPORT'], [`Period: ${period}`], ...(filtersLine ? [[filtersLine]] : []), [],
      ['', '', ...BLOCKS.flatMap(([, l]) => [l, '', ''])],
      ['Item Code', 'Item Name', ...BLOCKS.flatMap(() => ['Quantity', 'Price', 'Total Value'])]];
    data.rows.forEach(r => rows.push([r.itemCode, r.itemName, ...BLOCKS.flatMap(([k]) => [r[k].qty, r[k].price, r[k].value])]));
    rows.push([], ['', 'TOTAL', ...BLOCKS.flatMap(([k]) => [data.totals[k].qty, '', data.totals[k].value])]);
    await downloadXlsxFromAoa(rows, 'Stock Value', `stock-value-${data.startDate}-to-${data.endDate}.xlsx`, [{ wch: 18 }, { wch: 34 }, ...Array(12).fill({ wch: 13 })],
      { numberFormat: '#,##0.00', numberColumns: [5, 8, 11, 14] });
  };
  const print = () => printReport({
    title: 'Stock Value Report', subtitle: period, filtersLine, landscape: true,
    tableHtml: `<table><thead><tr><th rowspan="2">Item Code</th><th rowspan="2">Item Name</th>${BLOCKS.map(([, l]) => `<th colspan="3" style="text-align:center">${l}</th>`).join('')}</tr><tr>${BLOCKS.map(() => '<th class="r">Qty</th><th class="r">Price</th><th class="r">Value</th>').join('')}</tr></thead><tbody>${
      data.rows.map(r => `<tr><td>${esc(r.itemCode)}</td><td>${esc(r.itemName)}</td>${BLOCKS.map(([k]) => `<td class="r">${qty(r[k].qty)}</td><td class="r">${money(r[k].price)}</td><td class="r">${money(r[k].value)}</td>`).join('')}</tr>`).join('')
    }<tr class="total"><td></td><td>Total</td>${BLOCKS.map(([k]) => `<td class="r">${qty(data.totals[k].qty)}</td><td></td><td class="r">${money(data.totals[k].value)}</td>`).join('')}</tr></tbody></table>`,
    note: 'Values are the cost recorded on each stock movement; Opening + Received − Issued = Closing.',
  });

  return (
    <ReportShell
      title="Stock Value Report" subtitle="Opening, received, issued and closing stock — quantity, price and value" icon={CurrencyDollarIcon}
      loading={loading} onGenerate={generate} onReset={reset}
      filters={<>
        <Field label="Date From"><DateInput value={startDate} onChange={setStartDate} /></Field>
        <Field label="Date To"><DateInput value={endDate} onChange={setEndDate} /></Field>
        <Field label="Location"><WarehouseSelect value={warehouse.id} onChange={(id, label) => setWarehouse({ id, label: id ? label : '' })} /></Field>
        <ProductFilters value={product} onChange={setProduct} />
      </>}
      actions={data && <>
        <span className="text-sm text-gray-600"><b className="text-indigo-600">{period}</b> · {data.rows.length} items · closing value <b>Rs {money(data.totals.closing.value)}</b>
          {data.totals.reconciles ? <span className="ml-2 text-xs text-green-700">Opening + Received − Issued = Closing ✓</span> : <span className="ml-2 text-xs text-amber-700">Does not reconcile</span>}</span>
        <ExportButtons onExcel={exportExcel} onPrint={print} />
      </>}
    >
      {data && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200 text-xs text-gray-500">
              <tr>
                <th rowSpan={2} className="text-left px-4 py-2 font-medium">Item Code</th>
                <th rowSpan={2} className="text-left px-4 py-2 font-medium">Item Name</th>
                {BLOCKS.map(([k, l]) => <th key={k} colSpan={3} className="text-center px-3 pt-2 font-semibold border-l border-gray-200">{l}</th>)}
              </tr>
              <tr>{BLOCKS.map(([k]) => <React.Fragment key={k}><th className="text-right px-3 pb-2 font-medium border-l border-gray-200">Quantity</th><th className="text-right px-3 pb-2 font-medium">Price</th><th className="text-right px-3 pb-2 font-medium">Total Value</th></React.Fragment>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.rows.length === 0 ? <tr><td colSpan={14} className="px-4 py-8 text-center text-gray-400 italic">No stock for these filters</td></tr> : data.rows.map(r => (
                <tr key={r.itemId} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-mono text-xs whitespace-nowrap">{r.itemCode}</td>
                  <td className="px-4 py-2 min-w-[180px]">{r.itemName}</td>
                  {BLOCKS.map(([k]) => (
                    <React.Fragment key={k}>
                      <td className={`px-3 py-2 text-right font-mono border-l border-gray-100 ${k === 'closing' ? 'font-semibold' : ''}`}>{qty(r[k].qty)}</td>
                      <td className="px-3 py-2 text-right font-mono text-gray-500">{money(r[k].price)}</td>
                      <td className={`px-3 py-2 text-right font-mono ${k === 'closing' ? 'font-semibold' : ''}`}>{money(r[k].value)}</td>
                    </React.Fragment>
                  ))}
                </tr>
              ))}
              {data.rows.length > 0 && (
                <tr className="bg-gray-100 font-semibold">
                  <td colSpan={2} className="px-4 py-2">Total</td>
                  {BLOCKS.map(([k]) => <React.Fragment key={k}><td className="px-3 py-2 text-right font-mono border-l border-gray-200">{qty(data.totals[k].qty)}</td><td /><td className="px-3 py-2 text-right font-mono">{money(data.totals[k].value)}</td></React.Fragment>)}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </ReportShell>
  );
};

// Route page: period report (client's layout) and the existing as-of-date valuation
const StockValueReport = () => {
  const [mode, setMode] = useState('period');
  return (
    <div>
      <div className="px-4 sm:px-6 lg:px-8 pt-3 flex gap-1">
        {[['period', 'Period — Opening / Received / Issued / Closing'], ['snapshot', 'As of date — FIFO / LIFO / Average']].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setMode(k)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md ${mode === k ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'}`}>{l}</button>
        ))}
      </div>
      {mode === 'period' ? <StockValuePeriod /> : <StockValuationReport />}
    </div>
  );
};

export default StockValueReport;
